import { useCallback, useEffect, useRef, useState } from "react";
import { createRecognizer, speechRecognitionSupported } from "@/lib/speech";
import { diffWords } from "@/lib/scoring";

export const CALIBRATION_SENTENCE = "The quarterly report will be ready for review on Thursday morning.";
const QUIET_MS = 2500, SPEAK_MS = 6500;
const CHECK_KEY = "lexv_mic_check";

export const getLastMicCheck = () => { try { return JSON.parse(localStorage.getItem(CHECK_KEY)); } catch { return null; } };
export const saveMicCheck = (stats) => localStorage.setItem(CHECK_KEY, JSON.stringify({ stats, date: new Date().toISOString() }));
// Returns a warning object when the saved check found problems that would hurt speaking scores
export const micCheckWarning = (check) => {
  if (!check?.stats) return null;
  const s = check.stats;
  if (s.error) return { level: "bad", text: s.error === "denied" ? "Your last mic check was blocked by the browser. Allow microphone access and re-run the check." : "No microphone was found in your last check. Connect one and re-run the check." };
  if (s.noise > 0.03) return { level: "bad", text: "Your last mic check found high background noise. Move somewhere quieter and re-run the check before starting." };
  if (s.noise > 0.012) return { level: "warn", text: "Your last mic check picked up some background noise. A quieter room or headset mic will improve speaking scores." };
  if (s.voice < 0.02) return { level: "warn", text: "Your voice was very quiet in your last mic check. Sit closer to the microphone." };
  return null;
};

const median = (a) => { if (!a.length) return 0; const s = [...a].sort((x, y) => x - y); return s[Math.floor(s.length / 2)]; };

export const NOISE_BAD = 0.03, NOISE_WARN = 0.012;
export const noiseLevel = (n) => (n > NOISE_BAD ? "bad" : n > NOISE_WARN ? "warn" : "ok");
// Listens to the room (raw input, no noise suppression) and returns the median level, or null without a mic
export const measureNoise = async (ms = 3000, onLevel) => {
  let stream;
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }); }
  catch { return null; }
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  ctx.resume().catch(() => {});
  const an = ctx.createAnalyser(); an.fftSize = 1024;
  ctx.createMediaStreamSource(stream).connect(an);
  const buf = new Float32Array(an.fftSize), vals = [];
  return new Promise((res) => {
    let raf;
    const tick = () => {
      an.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      vals.push(rms); onLevel?.(Math.min(100, rms * 600));
      raf = requestAnimationFrame(tick);
    };
    tick();
    setTimeout(() => { cancelAnimationFrame(raf); stream.getTracks().forEach((t) => t.stop()); ctx.close().catch(() => {}); onLevel?.(0); res(median(vals)); }, ms);
  });
};
const topMean = (a, frac = 0.3) => { if (!a.length) return 0; const s = [...a].sort((x, y) => y - x).slice(0, Math.max(1, Math.floor(a.length * frac))); return s.reduce((x, y) => x + y, 0) / s.length; };

// Quiet phase measures room noise; speak phase measures voice level, clipping and recognition
export const useMicCalibration = () => {
  const [phase, setPhase] = useState("idle");
  const [level, setLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [stats, setStats] = useState(null);
  const cleanup = useRef(() => {});

  const stop = useCallback(() => { cleanup.current(); cleanup.current = () => {}; }, []);
  useEffect(() => stop, [stop]);

  const start = useCallback(async () => {
    stop();
    setStats(null); setTranscript("");
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } }); }
    catch (e) { const r = { error: e?.name === "NotAllowedError" ? "denied" : "nodevice" }; saveMicCheck(r); setPhase("error"); setStats(r); return; }
    const ctx = new AudioContext();
    const an = ctx.createAnalyser(); an.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Float32Array(an.fftSize);
    const quiet = [], speech = [];
    let clipFrames = 0, speakFrames = 0, cur = "quiet", raf, rec = null, heard = "";
    const tick = () => {
      an.getFloatTimeDomainData(buf);
      let sum = 0, peak = 0;
      for (let i = 0; i < buf.length; i++) { const v = Math.abs(buf[i]); sum += v * v; if (v > peak) peak = v; }
      const rms = Math.sqrt(sum / buf.length);
      setLevel(Math.min(100, rms * 600));
      if (cur === "quiet") quiet.push(rms);
      else { speech.push(rms); speakFrames++; if (peak > 0.98) clipFrames++; }
      raf = requestAnimationFrame(tick);
    };
    tick();
    setPhase("quiet");
    const t1 = setTimeout(() => {
      cur = "speak"; setPhase("speak");
      rec = createRecognizer((t) => { heard = t; setTranscript(t); });
      rec?.start();
    }, QUIET_MS);
    const t2 = setTimeout(() => {
      const result = { noise: median(quiet), voice: topMean(speech), clip: speakFrames ? clipFrames / speakFrames : 0, transcript: heard };
      stop();
      saveMicCheck(result);
      setStats(result); setPhase("done");
    }, QUIET_MS + SPEAK_MS);
    cleanup.current = () => { clearTimeout(t1); clearTimeout(t2); cancelAnimationFrame(raf); rec?.stop(); stream.getTracks().forEach((t) => t.stop()); ctx.close(); setLevel(0); };
  }, [stop]);

  const reset = useCallback(() => { stop(); setPhase("idle"); setStats(null); setTranscript(""); }, [stop]);
  return { phase, level, transcript, stats, start, stop, reset };
};

export const calibrationTips = (s) => {
  if (!s) return [];
  if (s.error === "denied") return [{ tone: "bad", title: "Allow microphone access", text: "Your browser blocked the microphone. Click the lock icon in the address bar, allow the microphone, then test again. You can still type spoken answers." }];
  if (s.error) return [{ tone: "bad", title: "No microphone found", text: "Plug in a headset or USB microphone and test again. Wired headsets with a boom mic give the most reliable results." }];
  const tips = [];
  const snr = s.voice / Math.max(s.noise, 0.0005);
  if (s.noise > 0.03) tips.push({ tone: "bad", title: "Reduce background noise", text: "Your room is noisy even when you're silent. Close windows and doors, switch off fans or TV, and move away from other people talking." });
  else if (s.noise > 0.012) tips.push({ tone: "warn", title: "Some background noise", text: "A little room noise was detected. A quieter spot or a headset microphone will help the scorer hear every word." });
  if (s.voice < 0.02) tips.push({ tone: "bad", title: "Speak closer to the microphone", text: "Your voice was very quiet. Move the mic 5–10 cm (2–4 in) from your mouth, or raise the input volume in your system sound settings." });
  else if (s.voice < 0.045) tips.push({ tone: "warn", title: "Speak a little louder", text: "Your voice level is on the low side. Speak with a confident, presentation-style voice or move slightly closer to the mic." });
  if (s.clip > 0.02) tips.push({ tone: "warn", title: "Too loud — audio is distorting", text: "Your voice is clipping. Move the mic a bit further away, keep it slightly to the side of your mouth, or lower the input volume." });
  if (s.voice >= 0.02 && snr < 3) tips.push({ tone: "warn", title: "Voice is close to the noise level", text: "Background sound is almost as loud as your voice. Reduce noise or move closer so your speech stands out clearly." });
  if (speechRecognitionSupported) {
    const d = diffWords(CALIBRATION_SENTENCE, s.transcript || "");
    const match = d.refLen ? d.matched / d.refLen : 0;
    if (!s.transcript) tips.push({ tone: "bad", title: "No words were recognised", text: "Read the sentence aloud as soon as the prompt appears. If you did speak, check that the right microphone is selected in your browser settings." });
    else if (match < 0.6) tips.push({ tone: "warn", title: "Speak more clearly and steadily", text: `Only ${Math.round(match * 100)}% of the sentence was recognised. Slow down slightly, finish each word fully and avoid trailing off at the end of the sentence.` });
    else tips.push({ tone: "good", title: "Speech recognised clearly", text: `${Math.round(match * 100)}% of the test sentence was recognised word-for-word.` });
  } else {
    tips.push({ tone: "good", title: "Recording ready", text: "This browser can't preview live captions, but your spoken answers are still recorded and transcribed automatically during the exam." });
  }
  if (!tips.some((t) => t.tone !== "good")) tips.unshift({ tone: "good", title: "Your setup sounds great", text: "Quiet room, clear voice level and no distortion. Keep the same position during the exam." });
  return tips;
};
